import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Question, Client } from '../../types';
import QuestionForm from './QuestionForm';
import './QuestionList.css';

interface QuestionListProps {
  clientId?: number | null;
}

const QuestionList: React.FC<QuestionListProps> = ({ clientId }) => {
  const { user, isAdmin } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId || null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClientId || (!isAdmin() && user?.clients?.length)) {
      fetchQuestions();
    }
  }, [selectedClientId, user?.clients]);

  const fetchClients = async () => {
    try {
      const response = await api.get<Client[]>('/clients');
      setClients(response.data);
      
      // Auto-select client for non-admin users
      if (!isAdmin() && response.data.length > 0 && !selectedClientId) {
        const firstClient = response.data[0];
        setSelectedClientId(firstClient.id);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedClientId) {
        params.client_id = selectedClientId;
      }
      
      const response = await api.get<Question[]>('/questions', { params });
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (question: Question) => {
    try {
      await api.patch(`/questions/${question.id}/status`, {
        status: !question.status,
      });
      fetchQuestions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update question status');
    }
  };

  const handleDelete = async (question: Question) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone if the question has reviews.')) {
      return;
    }

    try {
      await api.delete(`/questions/${question.id}`);
      fetchQuestions();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete question';
      alert(message);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingQuestion(null);
    fetchQuestions();
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setShowForm(true);
  };

  const filteredQuestions = questions.filter((q) =>
    q.question_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSelectedClientName = (): string => {
    if (!selectedClientId) return '';
    const client = clients.find((c) => c.id === selectedClientId);
    return client?.client_name || '';
  };

  return (
    <div className="questions-list">
      <div className="questions-header">
        <h2>Questions</h2>
        {getSelectedClientName() && (
          <div className="company-name-header">
            <span>Company: {getSelectedClientName()}</span>
          </div>
        )}
      </div>

      {isAdmin() && (
        <div className="client-selector-section">
          <label htmlFor="client-select">Select Company:</label>
          <select
            id="client-select"
            value={selectedClientId || ''}
            onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="client-select"
          >
            <option value="">All Companies</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="questions-actions">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        {selectedClientId && (
          <button
            onClick={() => {
              setEditingQuestion(null);
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            Add New Question
          </button>
        )}
      </div>

      {showForm && selectedClientId && (
        <QuestionForm
          question={editingQuestion}
          clientId={selectedClientId}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingQuestion(null);
          }}
        />
      )}

      {loading ? (
        <div className="loading">Loading questions...</div>
      ) : filteredQuestions.length === 0 ? (
        <div className="no-data">
          {selectedClientId ? 'No questions found' : 'Please select a company to view questions'}
        </div>
      ) : (
        <table className="questions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Question</th>
              {isAdmin() && <th>Company</th>}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.map((question) => (
              <tr key={question.id}>
                <td>{question.id}</td>
                <td className="question-title">{question.question_title}</td>
                {isAdmin() && (
                  <td>
                    {question.client ? (
                      <span className="client-name">{question.client.client_name}</span>
                    ) : (
                      <span className="no-client">—</span>
                    )}
                  </td>
                )}
                <td>
                  <span className={`status-badge ${question.status ? 'active' : 'inactive'}`}>
                    {question.status ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleToggleStatus(question)}
                      className={`btn btn-sm ${question.status ? 'btn-warning' : 'btn-success'}`}
                      title={question.status ? 'Deactivate' : 'Activate'}
                    >
                      {question.status ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleEdit(question)}
                      className="btn btn-sm btn-primary"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(question)}
                      className="btn btn-sm btn-danger"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default QuestionList;
